import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../styles/Jurisprudencia.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowTrendUp,
    faBalanceScale,
    faBuildingColumns,
    faCalendarDays,
    faCircleInfo,
    faCloudArrowUp,
    faFileLines,
    faFilter,
    faMagnifyingGlass,
    faRotateRight,
    faScaleBalanced,
    faXmark
} from '@fortawesome/free-solid-svg-icons';
import bgShapes from '../assets/BG-shapes-1.png';

const filters = ['Todos', 'TJSP'];
const jurisearchBaseUrl = (process.env.REACT_APP_JURISEARCH_URL || 'http://localhost:9080').replace(/\/$/, '');
const statusLabels = {
    idle: 'Aguardando documento',
    uploading: 'Enviando PDF',
    processing: 'Analisando',
    completed: 'Concluído',
    error: 'Falha na busca',
};

const normalizeText = (text = '') =>
    String(text)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const getField = (item, ...keys) => {
    for (const key of keys) {
        if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== '') {
            return item[key];
        }
    }
    return '';
};

const fallbackId = () => `juris-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const normalizeResult = (item) => {
    const score = Number(getField(item, 'combined_score', 'Pontuação de Relevância', 'pontuacao_relevancia')) || 0;
    return {
        id: getField(item, 'id', 'ID da Jurisprudência') || fallbackId(),
        tribunal: 'TJSP',
        classe: getField(item, 'classe', 'Classe') || 'Jurisprudência',
        tema: getField(item, 'orgao_julgador', 'Órgão Julgador') || 'Julgado',
        data: getField(item, 'data_julgamento', 'Data do Julgamento') || 'Data não informada',
        relator: getField(item, 'relator', 'Relator') || 'Relator não informado',
        ementa: getField(item, 'ementa', 'Ementa'),
        interpretacao: getField(item, 'ementa_interpretada', 'Ementa Interpretada'),
        processo: getField(item, 'numero_processo', 'Número do Processo'),
        score,
    };
};

const Jurisprudencia = () => {
    const fileInputRef = useRef(null);
    const eventSourceRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('Todos');
    const [selectedFile, setSelectedFile] = useState(null);
    const [status, setStatus] = useState('idle');
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('Selecione um PDF para iniciar a pesquisa no TJSP.');
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [processingTime, setProcessingTime] = useState(null);

    const filteredJurisprudencias = useMemo(() => {
        const query = normalizeText(searchTerm);

        return results.filter((item) => {
            const matchesFilter = activeFilter === 'Todos' || item.tribunal === activeFilter;
            const matchesSearch = [
                item.tribunal,
                item.classe,
                item.tema,
                item.data,
                item.relator,
                item.ementa,
                item.interpretacao,
                item.processo
            ].some((value) => normalizeText(value).includes(query));

            return matchesFilter && matchesSearch;
        });
    }, [activeFilter, results, searchTerm]);

    useEffect(() => () => eventSourceRef.current?.close(), []);

    const clearSearch = () => setSearchTerm('');

    const resetRun = () => {
        eventSourceRef.current?.close();
        eventSourceRef.current = null;
        setStatus('idle');
        setProgress(0);
        setProgressMessage('Selecione um PDF para iniciar a pesquisa no TJSP.');
        setSearchQuery('');
        setResults([]);
        setErrorMessage('');
        setProcessingTime(null);
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        setErrorMessage('');

        if (!file) {
            setSelectedFile(null);
            return;
        }

        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
            setSelectedFile(null);
            setErrorMessage('Selecione um arquivo PDF válido para executar o Jurisearch.');
            return;
        }

        setSelectedFile(file);
    };

    const completeWithResult = async (uploadId, streamedData) => {
        let data = streamedData;

        if (!data?.results) {
            const response = await fetch(`${jurisearchBaseUrl}/results/${uploadId}`);
            if (!response.ok) {
                throw new Error('Não foi possível carregar os resultados finais.');
            }
            data = await response.json();
        }

        const normalized = (data.results || []).map(normalizeResult).sort((a, b) => b.score - a.score);
        setSearchQuery(data.search_term || '');
        setProcessingTime(data.processing_time || null);
        setResults(normalized);
        setProgress(100);
        setStatus('completed');
        setProgressMessage(normalized.length > 0 ? 'Resultados ranqueados e prontos para revisão.' : 'Nenhum precedente aderente foi encontrado.');
        localStorage.setItem('jurisearch:last-run', JSON.stringify({
            searchTerm: data.search_term || '',
            resultCount: normalized.length,
            processingTime: data.processing_time || null,
            completedAt: new Date().toISOString(),
        }));
    };

    const runJurisearch = async () => {
        if (!selectedFile || status === 'uploading' || status === 'processing') return;

        resetRun();
        setStatus('uploading');
        setProgressMessage('Enviando o PDF para o Jurisearch...');

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await fetch(`${jurisearchBaseUrl}/upload`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const detail = await response.json().catch(() => null);
                throw new Error(detail?.detail || detail?.error || 'Falha no upload para o Jurisearch.');
            }

            const upload = await response.json();
            setStatus('processing');
            setProgress(3);
            setProgressMessage('Processamento iniciado...');

            const source = new EventSource(`${jurisearchBaseUrl}/stream/${upload.upload_id}`);
            eventSourceRef.current = source;

            source.onmessage = async (event) => {
                const data = JSON.parse(event.data);

                if (data.percent !== undefined) {
                    setProgress(data.percent);
                }
                if (data.message) {
                    setProgressMessage(data.message);
                }

                if (data.status === 'completed') {
                    source.close();
                    eventSourceRef.current = null;
                    await completeWithResult(upload.upload_id, data);
                }

                if (data.status === 'error') {
                    source.close();
                    eventSourceRef.current = null;
                    setStatus('error');
                    setErrorMessage(data.error || data.message || 'O processamento do Jurisearch falhou.');
                }
            };

            source.onerror = () => {
                source.close();
                eventSourceRef.current = null;
                setStatus('error');
                setErrorMessage('A conexão com o Jurisearch foi interrompida durante o processamento.');
            };
        } catch (error) {
            setStatus('error');
            setErrorMessage(error.message || 'O Jurisearch está indisponível.');
        }
    };

    const isBusy = status === 'uploading' || status === 'processing';
    const hasResults = results.length > 0;

    return (
        <div className={`main-container-jurisprudencia ${hasResults ? 'has-results' : ''}`}>
            <img className='bgshapes' src={bgShapes} alt='' aria-hidden='true' />

            <section className='container-jurisprudencia'>
                <header className='jurisprudencia-hero'>
                    <div>
                        <p className='jurisprudencia-eyebrow'>Jurisearch Beta</p>
                        <h1>Pesquisa jurisprudencial orientada pelo caso</h1>
                        <p className='jurisprudencia-subtitle'>
                            Envie o PDF do processo. O Jurisearch identifica a tese, consulta o TJSP e organiza
                            os precedentes pela aderência jurídica ao documento.
                        </p>
                    </div>

                    <div className='jurisprudencia-summary'>
                        <FontAwesomeIcon icon={faArrowTrendUp} />
                        <strong>{results.length}</strong>
                        <span>resultados ranqueados</span>
                    </div>
                </header>

                <div className='jurisprudencia-search-panel'>
                    <div className='jurisearch-upload-row'>
                        <input
                            ref={fileInputRef}
                            className='jurisearch-file-input'
                            type='file'
                            accept='.pdf,application/pdf'
                            onChange={handleFileChange}
                        />
                        <button className='jurisearch-upload-select' onClick={() => fileInputRef.current?.click()} type='button' disabled={isBusy}>
                            <FontAwesomeIcon icon={faCloudArrowUp} />
                            <span>{selectedFile ? selectedFile.name : 'Selecionar PDF'}</span>
                        </button>
                        <button className={`jurisprudencia-secondary-action ${isBusy ? 'is-processing' : ''}`} onClick={runJurisearch} type='button' disabled={!selectedFile || isBusy}>
                            <FontAwesomeIcon icon={isBusy ? faRotateRight : faMagnifyingGlass} />
                            {isBusy ? 'Pesquisando' : 'Iniciar pesquisa'}
                        </button>
                    </div>

                    <div className='jurisearch-progress' aria-live='polite'>
                        <div className='jurisearch-progress-bar'>
                            <span style={{ width: `${Math.min(progress, 100)}%` }} />
                        </div>
                        <p>{progressMessage}</p>
                    </div>

                    {errorMessage && (
                        <div className='jurisearch-error'>
                            <strong>Pesquisa indisponível</strong>
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    <label className='container-input-pesquisa-jurisprudencia'>
                        <FontAwesomeIcon className='icone-pesquisa-jurisprudencia' icon={faMagnifyingGlass} />
                        <input
                            type='text'
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder='Filtrar por classe, relator, processo ou conteúdo da ementa'
                            className='input-pesquisa-jurisprudencia'
                        />
                        {searchTerm && (
                            <button className='jurisprudencia-clear-search' onClick={clearSearch} type='button' aria-label='Limpar pesquisa'>
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        )}
                    </label>

                    <div className='jurisprudencia-filter-row' aria-label='Filtrar por tribunal'>
                        <span>
                            <FontAwesomeIcon icon={faFilter} />
                            Tribunal
                        </span>
                        {filters.map((filter) => (
                            <button
                                key={filter}
                                className={`jurisprudencia-filter ${activeFilter === filter ? 'active' : ''}`}
                                onClick={() => setActiveFilter(filter)}
                                type='button'
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                </div>

                <div className='jurisprudencia-content'>
                    <main className='resultado'>
                        <div className='resultado-header'>
                            <div>
                                <span>{filteredJurisprudencias.length} resultados</span>
                                <strong>{searchQuery ? `Consulta gerada: ${searchQuery}` : 'Precedentes ranqueados'}</strong>
                            </div>
                            <button className='jurisprudencia-secondary-action' onClick={resetRun} type='button'>
                                <FontAwesomeIcon icon={faRotateRight} />
                                Limpar
                            </button>
                        </div>

                        {filteredJurisprudencias.length > 0 ? (
                            <ul className='jurisprudencia-list'>
                                {filteredJurisprudencias.map((item) => (
                                    <li className='jurisprudencia-card' key={item.id}>
                                        <div className='jurisprudencia-card-icon'>
                                            <FontAwesomeIcon icon={faScaleBalanced} />
                                        </div>

                                        <div className='jurisprudencia-card-body'>
                                            <div className='jurisprudencia-card-title'>
                                                <span>{Math.round(item.score * 100)}% aderente</span>
                                                <strong>{item.classe}</strong>
                                            </div>

                                            <p>{item.ementa}</p>
                                            {item.interpretacao && <p className='jurisearch-interpretation'>{item.interpretacao}</p>}

                                            <div className='jurisprudencia-meta'>
                                                <span>
                                                    <FontAwesomeIcon icon={faBalanceScale} />
                                                    {item.tema}
                                                </span>
                                                <span>
                                                    <FontAwesomeIcon icon={faCalendarDays} />
                                                    {item.data}
                                                </span>
                                                <span>
                                                    <FontAwesomeIcon icon={faBuildingColumns} />
                                                    {item.relator}
                                                </span>
                                            </div>

                                            <div className='jurisprudencia-tags'>
                                                <span>{item.tribunal}</span>
                                                {item.processo && <span>{item.processo}</span>}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className='jurisprudencia-empty'>
                                <FontAwesomeIcon icon={faFileLines} />
                                <strong>{status === 'completed' ? 'Nenhum julgado encontrado' : 'Aguardando uma pesquisa'}</strong>
                                <span>Os julgados do TJSP aparecerão aqui em ordem de aderência.</span>
                            </div>
                        )}
                    </main>

                    <aside className='jurisprudencia-insights'>
                        <div className='insight-card insight-primary'>
                            <FontAwesomeIcon icon={faCircleInfo} />
                            <strong>Jimmy-A1</strong>
                            <p>Geração do termo, interpretação da ementa e pontuação jurídica com baixa latência.</p>
                        </div>

                        <div className='insight-card'>
                            <span className='insight-label'>Execução</span>
                            <strong>{statusLabels[status] || status}</strong>
                            <p>{processingTime ? `Concluída em ${processingTime.toFixed(1)} segundos.` : progressMessage}</p>
                        </div>

                        <div className='insight-card'>
                            <span className='insight-label'>Ranking híbrido</span>
                            <strong>60% jurídico + 40% semântico</strong>
                            <p>A pontuação combina a leitura do modelo com a proximidade vetorial entre o caso e a ementa.</p>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
};

export default Jurisprudencia;
